import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm, TimePicker, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";

const scopeOptions = [
  { value: "individual", label: "Individual (Employee)" },
  { value: "role", label: "Role" },
  { value: "department", label: "Department" },
  { value: "office", label: "Office" },
];

const WorkingHours = () => {
  const { request } = useRequest();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scope, setScope] = useState("office");
  const [form] = Form.useForm();

  const fetchItems = () => {
    request({ url: apiPath.workingHours, method: "GET", onSuccess: (res) => setItems(res?.data?.data || []) });
  };

  const fetchLookups = () => {
    request({ url: apiPath.roles, method: "GET", onSuccess: (res) => setRoles(res?.data?.data || []) });
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
    request({ url: apiPath.offices, method: "GET", onSuccess: (res) => setOffices(res?.data?.data || []) });
    request({ url: apiPath.employees, method: "GET", onSuccess: (res) => setEmployees(res?.data?.employees || []) });
  };

  useEffect(() => {
    fetchItems();
    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopeRefOptions = () => {
    if (scope === "role") return roles.map((r) => ({ value: r._id, label: r.name }));
    if (scope === "department") return departments.map((d) => ({ value: d._id, label: d.name }));
    if (scope === "office") return offices.map((o) => ({ value: o._id, label: o.name }));
    if (scope === "individual") return employees.map((e) => ({ value: e._id, label: `${e.name} (${e.employeeId})` }));
    return [];
  };

  const scopeRefLabel = (record) => {
    if (record.scope === "role") return roles.find((r) => r._id === record.scopeRefId)?.name || record.scopeRefId;
    if (record.scope === "department") return departments.find((d) => d._id === record.scopeRefId)?.name || record.scopeRefId;
    if (record.scope === "office") return offices.find((o) => o._id === record.scopeRefId)?.name || record.scopeRefId;
    if (record.scope === "individual") return employees.find((e) => e._id === record.scopeRefId)?.name || record.scopeRefId;
    return "";
  };

  const openCreate = () => {
    setEditing(null);
    setScope("office");
    form.resetFields();
    form.setFieldsValue({ scope: "office" });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setScope(record.scope);
    form.setFieldsValue({
      ...record,
      startTime: record.startTime ? moment(record.startTime, "HH:mm") : null,
      endTime: record.endTime ? moment(record.endTime, "HH:mm") : null,
    });
    setModalOpen(true);
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        startTime: values.startTime.format("HH:mm"),
        endTime: values.endTime.format("HH:mm"),
      };
      const url = editing ? `${apiPath.workingHours}/${editing._id}` : apiPath.workingHours;
      const method = editing ? "PUT" : "POST";
      request({
        url,
        method,
        data: payload,
        onSuccess: () => {
          message.success(editing ? "Working hours updated" : "Working hours created");
          setModalOpen(false);
          fetchItems();
        },
        onError: (err) => message.error(err?.response?.data?.message || "Save failed"),
      });
    });
  };

  const onDelete = (record) => {
    request({
      url: `${apiPath.workingHours}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Deleted");
        fetchItems();
      },
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Start", dataIndex: "startTime" },
    { title: "End", dataIndex: "endTime" },
    { title: "Scope", dataIndex: "scope", render: (s) => <Tag>{scopeOptions.find((o) => o.value === s)?.label || s}</Tag> },
    { title: "Applies To", render: (_, record) => scopeRefLabel(record) },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Delete this entry?" onConfirm={() => onDelete(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader
        title="Working Hours"
        subtitle="Define working-hour schedules by office, role, department or employee"
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Working Hours
          </Button>
        }
      />
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={items}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No working-hour schedules yet" actionLabel="Add Working Hours" onAction={openCreate} /> }}
      />

      <Modal title={editing ? "Edit Working Hours" : "Add Working Hours"} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={onSubmit} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="startTime" label="Start Time" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="endTime" label="End Time" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="scope" label="Scope" rules={[{ required: true }]}>
            <Select
              options={scopeOptions}
              onChange={(v) => {
                setScope(v);
                form.setFieldsValue({ scopeRefId: undefined });
              }}
            />
          </Form.Item>
          <Form.Item name="scopeRefId" label="Applies To" rules={[{ required: true }]}>
            <Select options={scopeRefOptions()} showSearch optionFilterProp="label" />
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default WorkingHours;
